import { Box, Button, Slider, Typography, ToggleButtonGroup } from "@mui/joy";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilterAcceptsReservations, setFilterHasWineGlasses, setFilterOpenNow, setFilterPrice } from "../../../store";
import FilterBarToggle from "./FilterBarToggle";
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';

function HorizontalFilterBar({ viewMode, setViewMode }) {
  const dispatch = useDispatch();

  // Get filter states from Redux
  const filterPrice = useSelector((state) => state.filter.price);
  const filterReservations = useSelector((state) => state.filter.acceptsReservations);
  const filterGlasses = useSelector((state) => state.filter.hasWineGlasses);
  const filterOpenNow = useSelector((state) => state.filter.openNow);

  // Popper states
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handlePriceClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Format price filter display
  const getPriceDisplay = () => {
    if (filterPrice === null) return "$50+";
    if (filterPrice === 0) return "Free";
    return `$${filterPrice}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        p: 1,
        borderBottom: "1px solid #e0e0e0",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {/* Left-aligned filter fields */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          ref={anchorRef}
          variant={filterPrice !== null ? "soft" : "outlined"}
          color="neutral"
          onClick={handlePriceClick}
          aria-describedby="corkage-fee-popper"
        >
          Corkage Fee: {getPriceDisplay()}
        </Button>

        <Popper
          id="corkage-fee-popper"
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1200 }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                paddingLeft: 4,
                paddingRight: 4,
                width: 250,
                borderRadius: '8px',
                mt: 0.5,
              }}
            >
              <Typography level="body-md" sx={{ mb: 1 }}>Corkage Fee</Typography>
              <Slider
                color="neutral"
                valueLabelDisplay="on"
                value={filterPrice ?? 50}
                step={5}
                min={0}
                max={50}
                marks={[
                  { value: 0, label: "Free" },
                  { value: 25, label: "$25" },
                  { value: 50, label: "$50+" },
                ]}
                onChange={(event, newValue) => {
                  dispatch(setFilterPrice(newValue === 50 ? null : newValue));
                }}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>

        <FilterBarToggle
          options={["Any time", "Open Now"]}
          value={filterOpenNow ? "Open Now" : "Any time"}
          onChange={(option) => {
            dispatch(setFilterOpenNow(option === "Open Now" ? true : null));
          }}
        />

        <FilterBarToggle
          options={["All", "Reservations"]}
          value={filterReservations ? "Reservations" : "All"}
          onChange={(option) => {
            dispatch(setFilterAcceptsReservations(option === "Reservations" ? true : null));
          }}
        />

        <FilterBarToggle
          options={["All", "Glasses"]}
          value={filterGlasses ? "Glasses" : "All"}
          onChange={(option) => {
            dispatch(setFilterHasWineGlasses(option === "Glasses" ? true : null));
          }}
        />
      </Box>

      {/* Right-aligned view mode toggle */}
      <ToggleButtonGroup
        value={viewMode}
        onChange={(event, newValue) => {
          if (newValue !== null) {
            setViewMode(newValue);
          }
        }}
      >
        <Button value="Split">Split</Button>
        <Button value="Map">Map</Button>
      </ToggleButtonGroup>
    </Box>
  );
}

export default HorizontalFilterBar;